/**
 * The adult chore screen: what needs approving, what everyone has banked, and the
 * chore list itself. Approving is the only thing that moves money — that's why it
 * sits at the top of the page.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiRequestError, money } from '../../lib/api';
import type {
  AllowanceEntry,
  Chore,
  ChoreCompletion,
  MemberSummary,
} from '../../shared/types';
import type { AdultData } from './ChoresPage';
import ChoreForm from './ChoreForm';
import PayoutForm from './PayoutForm';
import {
  Avatar,
  Banner,
  CADENCE_LABEL,
  Card,
  EmptyState,
  MemberChip,
  Modal,
  SectionTitle,
  btnPrimary,
  btnQuiet,
  btnSecondary,
  cx,
  memberById,
  shortDate,
} from './shared';

type Flash = { tone: 'ok' | 'error'; message: string } | null;

export default function AdultView({
  data,
  reload,
}: {
  data: AdultData;
  reload: () => Promise<void>;
}) {
  // `pending` is the Worker's full pending queue (?status=pending), not a slice of
  // recent completions — an old unapproved chore must never fall off the bottom.
  const { chores, pending, recent, members, balances } = data;

  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<Chore | null>(null);
  const [adding, setAdding] = useState(false);
  const [payingOut, setPayingOut] = useState<MemberSummary | null>(null);
  const [logging, setLogging] = useState<Chore | null>(null);
  const [openHistory, setOpenHistory] = useState<number | null>(null);

  const kids = members.filter((m) => m.role === 'child');

  /** `fn` returns the message to flash, so it can report what the SERVER actually did. */
  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key);
    setFlash(null);
    try {
      const message = await fn();
      await reload();
      setFlash({ tone: 'ok', message });
    } catch (err) {
      setFlash({
        tone: 'error',
        message:
          err instanceof ApiRequestError ? err.message : 'That did not go through. Try again.',
      });
    } finally {
      setBusy(null);
    }
  }

  const choreName = (id: number) => chores.find((ch) => ch.id === id)?.name ?? 'A chore';

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Chores</h1>
          <p className="mt-1 text-sm text-ink-soft">Approvals, rates, and allowance.</p>
        </div>
        <button type="button" onClick={() => setAdding(true)} className={btnPrimary}>
          Add chore
        </button>
      </div>

      {flash ? <Banner tone={flash.tone} message={flash.message} /> : null}

      {/* ---------------------------------------------------------- approvals */}
      <div>
        <SectionTitle
          action={
            pending.length > 1 ? (
              <button
                type="button"
                disabled={busy === 'approve-all'}
                onClick={() =>
                  void run('approve-all', async () => {
                    // The server approves every pending row, so the count it returns
                    // is the truth — not however many happened to be on screen.
                    const done = await api.post<ChoreCompletion[]>(
                      '/chores/completions/approve-all',
                      {}
                    );
                    const paid = done.reduce((sum, cc) => sum + cc.rate_at_time, 0);
                    if (done.length === 0) return 'Nothing was left to approve.';
                    return `Approved ${done.length} chore${done.length === 1 ? '' : 's'}${
                      paid > 0 ? ` — ${money(paid)} added to allowances.` : '.'
                    }`;
                  })
                }
                className={btnSecondary}
              >
                {busy === 'approve-all' ? 'Approving…' : 'Approve all'}
              </button>
            ) : undefined
          }
        >
          Needs approval{pending.length > 0 ? ` (${pending.length})` : ''}
        </SectionTitle>

        {pending.length === 0 ? (
          <EmptyState
            title="Nothing waiting on you"
            body="When someone checks off a chore, it lands here. Approving it is what puts the money in their allowance."
          />
        ) : (
          <ul className="space-y-3">
            {pending.map((cc) => {
              const member = memberById(members, cc.member_id);
              return (
                <li key={cc.id}>
                  <Card className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar member={member} />
                      <div className="min-w-0">
                        <p className="font-medium text-ink">{choreName(cc.chore_id)}</p>
                        <p className="text-sm text-ink-soft">
                          {member?.name ?? 'Someone'} · {shortDate(cc.completed_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="mr-1 font-display text-lg font-semibold text-honey tabular-nums">
                        {money(cc.rate_at_time)}
                      </span>
                      <button
                        type="button"
                        disabled={busy === `ap-${cc.id}`}
                        onClick={() =>
                          void run(`ap-${cc.id}`, async () => {
                            await api.post(`/chores/completions/${cc.id}/approve`);
                            return `Approved. ${money(cc.rate_at_time)} added to ${member?.name ?? 'their'} allowance.`;
                          })
                        }
                        className={cx(btnPrimary, 'flex-1 sm:flex-none')}
                      >
                        {busy === `ap-${cc.id}` ? 'Approving…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        disabled={busy === `rm-${cc.id}`}
                        onClick={() =>
                          void run(`rm-${cc.id}`, async () => {
                            await api.del(`/chores/completions/${cc.id}`);
                            return 'Sent back — no money moved. It stays in the history.';
                          })
                        }
                        className={btnQuiet}
                        aria-label={`Send back ${choreName(cc.chore_id)}`}
                      >
                        Send back
                      </button>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ---------------------------------------------------------- allowance */}
      <div>
        <SectionTitle>Allowance</SectionTitle>

        {balances.length === 0 ? (
          <EmptyState
            title="No kids set up yet"
            body="Add your kids in Settings → Family. Once they can sign in, their chores and allowance show up here."
            action={
              <Link to="/admin" className={btnPrimary}>
                Go to Settings
              </Link>
            }
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {balances.map((bal) => {
              const member = memberById(members, bal.member_id);
              if (!member) return null;
              return (
                <li key={bal.member_id}>
                  <Card>
                    <div className="flex items-center gap-3">
                      <Avatar member={member} size="lg" />
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-lg font-semibold text-ink">{member.name}</p>
                        <p className="text-sm text-ink-soft">
                          <span className="font-semibold text-sage tabular-nums">
                            {money(bal.balance)}
                          </span>{' '}
                          banked
                          {bal.pending > 0 ? (
                            <>
                              {' · '}
                              <span className="font-semibold text-honey tabular-nums">
                                {money(bal.pending)}
                              </span>{' '}
                              pending
                            </>
                          ) : null}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPayingOut(member)}
                        disabled={bal.balance <= 0}
                        className={cx(btnSecondary, 'flex-1')}
                      >
                        Pay out
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setOpenHistory(openHistory === member.id ? null : member.id)
                        }
                        aria-expanded={openHistory === member.id}
                        className={btnQuiet}
                      >
                        {openHistory === member.id ? 'Hide history' : 'History'}
                      </button>
                    </div>

                    {openHistory === member.id ? <HistoryList memberId={member.id} /> : null}
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ---------------------------------------------------------- chore list */}
      <div>
        <SectionTitle
          action={
            chores.length > 0 ? (
              <button type="button" onClick={() => setAdding(true)} className={btnSecondary}>
                Add chore
              </button>
            ) : undefined
          }
        >
          The list
        </SectionTitle>

        {chores.length === 0 ? (
          <EmptyState
            title="No chores yet"
            body={
              kids.length === 0
                ? 'Add your first chore — name it, set what it pays, and leave it open to anyone until the kids are set up.'
                : 'Add your first chore — name it, set what it pays, and assign it (or leave it open to anyone).'
            }
            action={
              <button type="button" onClick={() => setAdding(true)} className={btnPrimary}>
                Add the first chore
              </button>
            }
          />
        ) : (
          <Card className="p-0">
            <ul className="divide-y divide-border">
              {chores.map((chore) => (
                <li
                  key={chore.id}
                  className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3">
                      <p className="font-medium text-ink">{chore.name}</p>
                      <span className="font-semibold text-honey tabular-nums">
                        {money(chore.rate)}
                      </span>
                    </div>
                    {chore.description ? (
                      <p className="mt-0.5 text-sm text-ink-soft">{chore.description}</p>
                    ) : null}
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-ink-faint">
                      <MemberChip member={memberById(members, chore.assigned_to)} />
                      <span aria-hidden="true">·</span>
                      <span>{CADENCE_LABEL[chore.cadence]}</span>
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setLogging(chore)}
                      className={btnQuiet}
                      aria-label={`Log a completion of ${chore.name}`}
                    >
                      Log done
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(chore)}
                      className={btnQuiet}
                      aria-label={`Edit ${chore.name}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={busy === `del-${chore.id}`}
                      onClick={() =>
                        void run(`del-${chore.id}`, async () => {
                          await api.del(`/chores/${chore.id}`);
                          return `Removed “${chore.name}”. Everything already earned stays put.`;
                        })
                      }
                      className={btnQuiet}
                      aria-label={`Remove ${chore.name}`}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {/* ---------------------------------------------------------- recently approved */}
      {recent.length > 0 ? (
        <div>
          <SectionTitle>Recently approved</SectionTitle>
          <Card className="p-0">
            <ul className="divide-y divide-border">
              {recent.map((cc) => {
                const member = memberById(members, cc.member_id);
                return (
                  <li key={cc.id} className="flex items-center justify-between gap-4 px-5 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar member={member} size="sm" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {choreName(cc.chore_id)}
                        </p>
                        <p className="text-xs text-ink-faint">
                          {member?.name ?? 'Someone'} · {shortDate(cc.completed_at)}
                        </p>
                      </div>
                    </div>
                    <p className="shrink-0 text-sm font-semibold text-sage tabular-nums">
                      +{money(cc.rate_at_time)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      ) : null}

      {adding ? (
        <ChoreForm
          chore={null}
          members={members}
          onClose={() => setAdding(false)}
          onSaved={reload}
        />
      ) : null}

      {editing ? (
        <ChoreForm
          chore={editing}
          members={members}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      ) : null}

      {payingOut ? (
        <PayoutForm
          member={payingOut}
          balance={
            balances.find((b) => b.member_id === payingOut.id) ?? {
              member_id: payingOut.id,
              balance: 0,
              pending: 0,
            }
          }
          onClose={() => setPayingOut(null)}
          onSaved={reload}
        />
      ) : null}

      {logging ? (
        <LogDoneDialog
          chore={logging}
          members={members}
          onClose={() => setLogging(null)}
          onDone={async (member) => {
            await run(`log-${logging.id}`, async () => {
              await api.post(`/chores/${logging.id}/complete`, { member_id: member.id });
              return `Logged for ${member.name}. Approve it below to pay it out.`;
            });
            setLogging(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** "Who did it?" — for the times an adult checks a chore off on a kid's behalf. */
function LogDoneDialog({
  chore,
  members,
  onClose,
  onDone,
}: {
  chore: Chore;
  members: MemberSummary[];
  onClose: () => void;
  onDone: (member: MemberSummary) => Promise<void>;
}) {
  const candidates =
    chore.assigned_to != null
      ? members.filter((m) => m.id === chore.assigned_to)
      : members.filter((m) => m.role === 'child');
  const list = candidates.length > 0 ? candidates : members;

  return (
    <Modal title={`Who did “${chore.name}”?`} onClose={onClose}>
      <ul className="space-y-2">
        {list.map((m) => (
          <li key={m.id}>
            <button
              type="button"
              onClick={() => void onDone(m)}
              className="flex min-h-[56px] w-full items-center gap-3 rounded-xl border border-border bg-surface px-4 text-left transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              <Avatar member={m} />
              <span className="font-medium text-ink">{m.name}</span>
              <span className="ml-auto font-semibold text-honey tabular-nums">
                {money(chore.rate)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-ink-faint">
        This creates a completion waiting for approval — it doesn't pay out until you approve it.
      </p>
    </Modal>
  );
}

/** Ledger for one member, loaded on demand. */
function HistoryList({ memberId }: { memberId: number }) {
  const [entries, setEntries] = useState<AllowanceEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setEntries(null);
    setError(null);
    api
      .get<AllowanceEntry[]>(`/chores/allowance/history?member_id=${memberId}&limit=20`)
      .then((rows) => {
        if (live) setEntries(rows);
      })
      .catch(() => {
        if (live) setError('Could not load the history.');
      });
    return () => {
      live = false;
    };
  }, [memberId]);

  if (error) return <p className="mt-4 text-sm text-clay">{error}</p>;
  if (entries === null)
    return (
      <p role="status" className="mt-4 text-sm text-ink-faint">
        Loading history…
      </p>
    );
  if (entries.length === 0)
    return <p className="mt-4 text-sm text-ink-faint">Nothing earned or paid out yet.</p>;

  return (
    <ul className="mt-4 divide-y divide-border border-t border-border">
      {entries.map((e) => (
        <li key={e.id} className="flex items-center justify-between gap-3 py-2">
          <div className="min-w-0">
            <p className="truncate text-sm text-ink">{e.reason}</p>
            <p className="text-xs text-ink-faint">{shortDate(e.created_at)}</p>
          </div>
          <p
            className={cx(
              'shrink-0 text-sm font-semibold tabular-nums',
              e.delta >= 0 ? 'text-sage' : 'text-clay'
            )}
          >
            {e.delta >= 0 ? '+' : '−'}
            {money(Math.abs(e.delta))}
          </p>
        </li>
      ))}
    </ul>
  );
}
